import { useEffect, useState } from 'react';

import type { IClientContact, IClientDataCardData } from '../../interfaces/clients';
import type { IProspect } from '../../interfaces/prospects';
import Clients from '../../services/api/clients/clients';
import Prospects from '../../services/api/prospects/prospects';
import { ClientsStatus } from '../../types/clients';
import { useUser } from '../useUser/useUser';

export const useClientContacts = (status: ClientsStatus | null, id: number | null) => {
  const { brokerId, loading: userLoading } = useUser();
  const [contacts, setContacts] = useState<IClientContact[]>([]);
  const [prospect, setProspect] = useState<IProspect | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    if (userLoading || !status) return;

    let cancelled = false;

    const run = async () => {
      setLoading(true);
      setError(false);
      try {
        if (status === ClientsStatus.Active) {
          const result = !brokerId || id === null ? [] : await Clients.getClientContacts(brokerId, id);
          if (!cancelled) setContacts(result);
        } else {
          const result = !brokerId || id === null ? [] : await Prospects.getProspects(brokerId, { prospectId: id });
          if (!cancelled) setProspect(result[0] ?? null);
        }
      } catch {
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [brokerId, id, status, userLoading, reloadToken]);

  const retry = () => {
    setError(false);
    setLoading(true);
    setReloadToken((token) => token + 1);
  };

  let data: IClientDataCardData | null = null;
  if (status === ClientsStatus.Active) {
    data = { status, contacts };
  } else if (status === ClientsStatus.AwaitingStart) {
    data = { status, prospect };
  }

  return { data, loading: loading || userLoading, error, retry };
};
